import axios from "axios";
import { fetchAuthSession } from 'aws-amplify/auth';
import { refreshToken, isTokenExpiring, handleTokenExpiration } from './tokenManager';
import { getConfig } from 'common/config';
import {
  applyCustomerContextHeaders,
  attachCustomerHeaderOmissionInterceptor,
  clearCustomerContextHeaders,
} from "./CustomerContextHeaders";

const base_url = getConfig('REACT_APP_BASE_URL');

const httpClient = axios.create({
  baseURL: base_url,
  responseType: "json",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

httpClient.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();
      
      if (!session.tokens || !session.tokens.accessToken) {
        return config;
      }
      
      if (isTokenExpiring(session.tokens.accessToken)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          return config;
        }
      }
      
      const token = session.tokens.accessToken.toString();
      config.headers.Authorization = `Bearer ${token}`;
      
      httpClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } catch (error) {
      console.warn("Could not refresh token from Amplify session:", error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

attachCustomerHeaderOmissionInterceptor(httpClient);

// Add a response interceptor
httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      await handleTokenExpiration();
    }
    return Promise.reject(
      error instanceof Error ? error : Error("unknown error")
    );
  }
);

const setAuthToken = (token) => {
  if (!token) {
    delete httpClient.defaults.headers.common.Authorization;
    clearCustomerContextHeaders();
    return;
  }
  httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;
};

const setTokenToHeader = (userid, token, customerCode) => {
  setAuthToken(token);
  if (!token) {
    return;
  }
  if (customerCode && userid) {
    applyCustomerContextHeaders(userid, customerCode);
  }
};

const changeHeaderToken = (userId, customerCode) => {
  if (customerCode == null || String(customerCode).trim() === "") {
    clearCustomerContextHeaders();
    return;
  }
  const resolvedUserId =
    userId ?? httpClient.defaults.headers.common["user-id"];
  applyCustomerContextHeaders(resolvedUserId, customerCode);
};

export {
  base_url,
  httpClient,
  setAuthToken,
  setTokenToHeader,
  changeHeaderToken,
  clearCustomerContextHeaders,
  applyCustomerContextHeaders,
};
